import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '@/config';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Search, Plus, Upload, Eye, Download, FileImage, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

export const ReportManagement = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [reports, setReports] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    patientId: '',
    reportName: '',
    visitDate: '',
    file: null
  });
  const [fileName, setFileName] = useState('');
  const [uploadLoading, setUploadLoading] = useState(false);

  const fetchReports = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/reports`);
      setReports(response.data || []);
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPatients = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/patients`);
      setPatients(response.data?.value || response.data || []);
    } catch (error) {
      console.error('Error fetching patients:', error);
    }
  };

  useEffect(() => {
    fetchReports();
    fetchPatients();
  }, []);

  const fmt = (d) => d ? new Date(d).toLocaleDateString() : '—';

  const filteredReports = reports.filter(report => {
    const patientName = (report.patientName || report.patient_name || '').toLowerCase();
    const reportName = (report.reportName || report.name || '').toLowerCase();
    const normalizedSearch = searchTerm.toLowerCase();
    return (
      patientName.includes(normalizedSearch) ||
      reportName.includes(normalizedSearch)
    );
  });

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadForm({ ...uploadForm, file });
      setFileName(file.name);
    }
  };

  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    if (!uploadForm.file) {
      toast.error('Please select a file to upload');
      return;
    }
    if (!uploadForm.patientId) {
      toast.error('Please select a patient');
      return;
    }

    const selectedPatient = patients.find(p => p.id === Number(uploadForm.patientId));

    try {
      setUploadLoading(true);
      const formDataUpload = new FormData();
      formDataUpload.append('file', uploadForm.file);
      formDataUpload.append('patient_id', uploadForm.patientId);
      formDataUpload.append('patient_name', selectedPatient?.name || '');
      formDataUpload.append('report_name', uploadForm.reportName || 'Unnamed Report');
      formDataUpload.append('visit_date', uploadForm.visitDate || '');

      await axios.post(`${API_BASE_URL}/api/reports/upload`, formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Report uploaded successfully!');
      setIsUploadOpen(false);
      setUploadForm({ patientId: '', reportName: '', visitDate: '', file: null });
      setFileName('');

      // Refresh reports list
      const response = await axios.get(`${API_BASE_URL}/api/reports`);
      setReports(response.data);
    } catch (error) {
      console.error('Error uploading report:', error);
      toast.error('Failed to upload report');
    } finally {
      setUploadLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
        <div className="flex items-start justify-between gap-3 w-full sm:w-auto">
          <div>
            <h1 className="font-serif text-2xl sm:text-3xl font-bold mb-1 sm:mb-2">Medical Reports</h1>
            <p className="text-xs sm:text-sm text-muted-foreground">Upload and manage patient medical reports</p>
          </div>
          {/* Mobile Icon-only Refresh Button */}
          <Button
            variant="outline"
            size="icon"
            onClick={fetchReports}
            disabled={loading}
            className="sm:hidden h-9 w-9 flex-shrink-0"
            aria-label="Refresh reports"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto">
          {/* Desktop Refresh Button */}
          <Button
            variant="outline"
            onClick={fetchReports}
            disabled={loading}
            className="hidden sm:inline-flex h-11 gap-1.5"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Dialog open={isUploadOpen} onOpenChange={setIsUploadOpen}>
            <DialogTrigger asChild>
              <Button className="w-full sm:w-auto h-11">
                <Plus className="h-4 w-4 mr-2" />
                Upload Report
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-md w-[95vw] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-serif text-xl sm:text-2xl">Upload Medical Report</DialogTitle>
              <DialogDescription className="text-xs sm:text-sm">
                Upload lab reports, X-rays, or other medical documents
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleUploadSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="patient" className="text-sm">Select Patient *</Label>
                <Select
                  value={uploadForm.patientId}
                  onValueChange={(value) => setUploadForm({ ...uploadForm, patientId: value })}
                  required
                >
                  <SelectTrigger className="h-11">
                    <SelectValue placeholder="Choose patient" />
                  </SelectTrigger>
                  <SelectContent>
                    {patients.map(patient => (
                      <SelectItem key={patient.id} value={patient.id.toString()}>
                        {patient.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="reportName" className="text-sm">Report Name *</Label>
                <Input
                  id="reportName"
                  placeholder="e.g., Blood Test Report"
                  className="h-11"
                  value={uploadForm.reportName}
                  onChange={(e) => setUploadForm({ ...uploadForm, reportName: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="visitDate" className="text-sm">Visit Date *</Label>
                <Input
                  id="visitDate"
                  type="date"
                  className="h-11"
                  value={uploadForm.visitDate}
                  onChange={(e) => setUploadForm({ ...uploadForm, visitDate: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="file" className="text-sm">Upload File *</Label>
                <div className="border-2 border-dashed border-border rounded-lg p-5 sm:p-6 text-center hover:border-primary/50 transition-colors">
                  <div className="flex flex-col items-center gap-2">
                    <FileImage className="h-8 w-8 text-muted-foreground" />
                    <Label
                      htmlFor="uploadFile"
                      className="cursor-pointer text-primary hover:underline font-medium text-sm"
                    >
                      Click to upload
                    </Label>
                    <p className="text-xs text-muted-foreground">PDF, JPG, PNG up to 10MB</p>
                    <Input
                      id="uploadFile"
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      className="hidden"
                      onChange={handleFileChange}
                      required
                    />
                    {fileName && (
                      <p className="text-xs font-medium text-foreground mt-1">
                        {fileName}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-2">
                <Button type="button" variant="outline" className="w-full sm:w-auto h-11" onClick={() => setIsUploadOpen(false)} disabled={uploadLoading}>
                  Cancel
                </Button>
                <Button type="submit" isLoading={uploadLoading} className="w-full sm:w-auto h-11">
                  <Upload className="h-4 w-4 mr-2" />
                  Upload
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Search */}
      <Card className="shadow-card">
        <CardContent className="p-4 sm:p-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search by patient name or report name..."
              className="pl-10 h-11 text-sm sm:text-base"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Reports List */}
      <Card className="shadow-card">
        <CardHeader className="border-b border-border py-4 px-4 sm:px-6">
          <CardTitle className="font-serif text-xl">All Reports ({filteredReports.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {/* Mobile Card List (< md) */}
          <div className="block md:hidden divide-y divide-border">
            {filteredReports.length > 0 ? (
              filteredReports.map((report) => (
                <div key={report.id} className="p-4 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-semibold text-slate-900 text-base">
                        {report.report_name || report.reportName || 'Unnamed Report'}
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        Patient: <span className="font-medium text-slate-700">{report.patient_name || report.patientName}</span>
                      </div>
                    </div>
                    <Badge variant={report.status === 'Completed' ? 'default' : 'outline'} className="text-xs flex-shrink-0">
                      {report.status}
                    </Badge>
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-xs bg-muted/40 p-2.5 rounded-lg">
                    <div>
                      <span className="text-muted-foreground block text-[11px]">Visit Date</span>
                      <span className="font-medium text-slate-700">{report.visit_date || report.visitDate || 'N/A'}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground block text-[11px]">Upload Date</span>
                      <span className="font-medium text-slate-700">{fmt(report.date)}</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 pt-1">
                    <Button
                      variant="outline"
                      className="h-11 text-sm font-medium gap-1.5"
                      onClick={() => report.file_url ? window.open(report.file_url, '_blank') : navigate(`/admin/reports/${report.id}`)}
                    >
                      <Eye className="h-4 w-4" />
                      View
                    </Button>
                    <Button
                      variant="secondary"
                      className="h-11 text-sm font-medium gap-1.5"
                      onClick={() => report.file_url ? window.open(report.file_url, '_blank') : navigate(`/admin/reports/${report.id}`)}
                    >
                      <Download className="h-4 w-4" />
                      Download
                    </Button>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-muted-foreground text-sm p-4">
                No reports found.
              </div>
            )}
          </div>

          {/* Desktop Table (md+) */}
          <div className="hidden md:block overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Patient Name</TableHead>
                  <TableHead>Report Name</TableHead>
                  <TableHead>Upload Date</TableHead>
                  <TableHead>Visit Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredReports.length > 0 ? (
                  filteredReports.map((report) => (
                    <TableRow key={report.id}>
                      <TableCell className="font-medium">{report.patient_name || report.patientName}</TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="font-medium">{report.report_name || report.reportName || 'Unnamed Report'}</span>
                        </div>
                      </TableCell>
                      <TableCell>{fmt(report.date)}</TableCell>
                      <TableCell>{report.visit_date || report.visitDate || 'N/A'}</TableCell>
                      <TableCell>
                        <Badge variant={report.status === 'Completed' ? 'default' : 'outline'}>{report.status}</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-center gap-2">
                          <Button variant="ghost" size="sm" onClick={() => report.file_url ? window.open(report.file_url, '_blank') : navigate(`/admin/reports/${report.id}`)}>
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => report.file_url ? window.open(report.file_url, '_blank') : navigate(`/admin/reports/${report.id}`)}>
                            <Download className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      No reports found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ReportManagement;
